import { useDelete, usePatch, usePost, usePut } from "@albertine/api-client";
import type {
    ArchiveMemberRequest,
    CancelMembershipRequest,
    ConvertTrialToPaidRequest,
    CreateMemberLinkRequest,
    EditLimitedProfileFamilyMemberRequest,
    EditMemberContactDetailsRequest,
    EditMemberContributionsRequest,
    EditMemberDiningDetailsRequest,
    EditMemberEventsDetailsRequest,
    EditMemberFamilyDetailsRequest,
    EditMemberPaymentDetailsRequest,
    EditMemberPersonalInfoRequest,
    EditMemberShoppingDetailsRequest,
    EditMemberTravelDetailsRequest,
    EditMemberWellnessDetailsRequest,
    ExtendTrialRequest,
    LinkFamilyMemberProfileRequest,
    LinkLimitedProfileFamilyMemberRequest,
    ManageMemberRequest,
    UnlinkFamilyMemberProfileRequest,
    UpdateMemberLinkRequest,
    UpdateMemberMembershipRequest,
} from "@albertine/types/openapi/lmt";
import type {
    MemberProfileStore,
    ReferralPromotionRequest,
} from "@prototypes/member-profile/MemberProfileContext";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import {
    invalidateMember,
    invalidateMemberAndListAllMembers,
} from "../../common/hooks/useMember";
import { EventEmitter } from "../../EventEmitter";
import { toApiListenerWithToastMessages } from "./member-profile-actions.util";

export type MemberProfileUpdates = Omit<
    MemberProfileStore,
    | "profile"
    | "overlay"
    | "setOverlay"
    | "agents"
    | "groups"
    | "members"
    | "membershipTiers"
    | "isAdmin"
    | "findStripeCustomer"
    | "openProactivityMessage"
>;

export function useMemberProfileUpdates(
    memberId: string,
    memberName: string,
): MemberProfileUpdates {
    const queryClient = useQueryClient();

    const { mutate: manageMember, isPending: isManagePending } = usePatch(
        "manageMember",
        invalidateMemberAndListAllMembers,
    );
    const { mutate: editPersonalInfo, isPending: isPersonalPending } = usePost(
        "agentEditMemberPersonalInfo",
        invalidateMemberAndListAllMembers,
    );
    const { mutate: editContactDetails, isPending: isContactPending } = usePost(
        "agentEditMemberContactDetails",
        invalidateMember,
    );
    const { mutate: editAdmin, isPending: isAdminPending } = usePost(
        "agentEditMemberContributions",
        invalidateMember,
    );
    const { mutate: editFamilyDetails, isPending: isFamilyPending } = usePost(
        "agentEditMemberFamilyDetails",
        invalidateMember,
    );
    const { mutate: linkLimitedFamily, isPending: isLinkLimitedPending } = usePost(
        "agentLinkLimitedFamilyMemberProfile",
        invalidateMember,
    );
    const { mutate: editLimitedFamilyMember, isPending: isEditLimitedPending } =
        usePatch("agentEditLimitedFamilyMemberProfile", invalidateMember);
    const { mutate: unlinkLimitedFamily, isPending: isUnlinkLimitedPending } =
        useDelete("agentUnlinkLimitedFamilyMemberProfile", invalidateMember);
    const { mutate: linkFamily, isPending: isLinkFamilyPending } = usePost(
        "agentLinkFamilyMemberProfile",
        invalidateMember,
    );
    const { mutate: unlinkFamily, isPending: isUnlinkFamilyPending } = usePost(
        "agentUnlinkFamilyMemberProfile",
        invalidateMember,
    );
    const { mutate: editDining, isPending: isDiningPending } = usePost(
        "agentEditMemberDiningDetails",
        invalidateMember,
    );
    const { mutate: editTravel, isPending: isTravelPending } = usePost(
        "agentEditMemberTravelDetails",
        invalidateMember,
    );
    const { mutate: editEvents, isPending: isEventsPending } = usePost(
        "agentEditMemberEventsDetails",
        invalidateMember,
    );
    const { mutate: editWellness, isPending: isWellnessPending } = usePost(
        "agentEditMemberWellnessDetails",
        invalidateMember,
    );
    const { mutate: editShopping, isPending: isShoppingPending } = usePost(
        "agentEditMemberShoppingDetails",
        invalidateMember,
    );
    const { mutate: editPayments, isPending: isPaymentsPending } = usePatch(
        "agentEditMemberPaymentDetails",
    );
    const { mutate: createMemberLink, isPending: isCreateLinkPending } = usePost(
        "agentCreateMemberLink",
        invalidateMember,
    );
    const { mutate: updateMemberLink, isPending: isUpdateLinkPending } = usePatch(
        "agentUpdateMemberLink",
        invalidateMember,
    );
    const { mutate: deleteMemberLink, isPending: isDeleteLinkPending } = useDelete(
        "agentDeleteMemberLink",
        invalidateMember,
    );
    const { mutate: archiveMember, isPending: isArchivePending } = usePost(
        "agentArchiveMember",
        invalidateMemberAndListAllMembers,
    );
    const { mutate: activateMember, isPending: isActivatePending } = usePost(
        "agentActivateMember",
        invalidateMemberAndListAllMembers,
    );
    const { mutate: extendTrialMutation, isPending: isExtendTrialPending } = usePost(
        "extendTrial",
        invalidateMemberAndListAllMembers,
    );
    const { mutate: convertTrialToPaid, isPending: isUpgradePending } = usePost(
        "convertTrialToPaid",
        invalidateMemberAndListAllMembers,
    );
    const { mutate: updateMembership, isPending: isMembershipPending } = usePut(
        "updateMemberMembership",
        invalidateMemberAndListAllMembers,
    );
    const { mutate: cancelMembership, isPending: isCancelPending } = usePost(
        "cancelMembership",
        invalidateMemberAndListAllMembers,
    );
    const {
        mutate: setOnboardingPhoneCompletion,
        isPending: isCompleteByPhonePending,
    } = usePost("setMemberOnboardingPhoneCompletionState", invalidateMember);
    const { mutate: createReferralPromotion, isPending: isCreateReferralPending } =
        usePost("createMemberReferralPromotion");
    const { mutate: updateReferralPromotion, isPending: isUpdateReferralPending } =
        usePatch("updateMemberReferralPromotion");

    return {
        saving:
            isManagePending ||
            isPersonalPending ||
            isContactPending ||
            isAdminPending ||
            isFamilyPending ||
            isLinkLimitedPending ||
            isEditLimitedPending ||
            isUnlinkLimitedPending ||
            isLinkFamilyPending ||
            isUnlinkFamilyPending ||
            isDiningPending ||
            isTravelPending ||
            isEventsPending ||
            isWellnessPending ||
            isShoppingPending ||
            isPaymentsPending ||
            isCreateLinkPending ||
            isUpdateLinkPending ||
            isDeleteLinkPending ||
            isArchivePending ||
            isActivatePending ||
            isExtendTrialPending ||
            isUpgradePending ||
            isMembershipPending ||
            isCancelPending ||
            isCompleteByPhonePending ||
            isCreateReferralPending ||
            isUpdateReferralPending,
        saveManage(payload: ManageMemberRequest, onSuccess: () => void) {
            manageMember(
                { param: { memberId }, json: payload },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        savePersonal(payload: EditMemberPersonalInfoRequest, onSuccess: () => void) {
            editPersonalInfo(
                { param: { memberId }, json: payload },
                toApiListenerWithToastMessages(() => {
                    void queryClient.invalidateQueries({
                        queryKey: ["memberPreferences", { param: { memberId } }],
                    });
                    void queryClient.invalidateQueries({
                        queryKey: [
                            "memberOnboardingPreferences",
                            { param: { memberId } },
                        ],
                    });
                    onSuccess();
                }),
            );
        },
        saveContact(payload: EditMemberContactDetailsRequest, onSuccess: () => void) {
            editContactDetails(
                { param: { memberId }, json: payload },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        saveAdmin(payload: EditMemberContributionsRequest, onSuccess: () => void) {
            editAdmin(
                { param: { memberId }, json: payload },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        saveFamilyNotes(
            payload: EditMemberFamilyDetailsRequest,
            onSuccess: () => void,
        ) {
            editFamilyDetails(
                { param: { memberId }, json: payload },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        createLimitedFamily(
            payload: LinkLimitedProfileFamilyMemberRequest,
            onSuccess: () => void,
        ) {
            linkLimitedFamily(
                { param: { memberId }, json: payload },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        editLimitedFamily(
            familyMemberId: string,
            payload: EditLimitedProfileFamilyMemberRequest,
            onSuccess: () => void,
        ) {
            editLimitedFamilyMember(
                {
                    param: { memberId, profileId: familyMemberId },
                    json: payload,
                },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        deleteLimitedFamily(familyMemberId: string, onSuccess: () => void) {
            unlinkLimitedFamily(
                { param: { memberId, profileId: familyMemberId } },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        createLinkedFamily(
            payload: LinkFamilyMemberProfileRequest,
            onSuccess: () => void,
        ) {
            linkFamily(
                { param: { memberId }, json: payload },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        replaceLinkedFamily(
            memberIdToUnlink: string,
            payload: LinkFamilyMemberProfileRequest,
            onSuccess: () => void,
        ) {
            unlinkFamily(
                { param: { memberId }, json: { memberIdToUnlink } },
                {
                    onSuccess: () => {
                        linkFamily(
                            { param: { memberId }, json: payload },
                            toApiListenerWithToastMessages(onSuccess),
                        );
                    },
                    onError: (error) => {
                        EventEmitter.emit({
                            name: "showToastMessage",
                            params: {
                                type: "error",
                                message: `Unlinking family member failed. ${error.error}`,
                            },
                        });
                    },
                },
            );
        },
        deleteLinkedFamily(
            payload: UnlinkFamilyMemberProfileRequest,
            onSuccess: () => void,
        ) {
            unlinkFamily(
                { param: { memberId }, json: payload },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        saveDining(payload: EditMemberDiningDetailsRequest, onSuccess: () => void) {
            editDining(
                { param: { memberId }, json: payload },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        saveTravel(payload: EditMemberTravelDetailsRequest, onSuccess: () => void) {
            editTravel(
                { param: { memberId }, json: payload },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        saveEvents(payload: EditMemberEventsDetailsRequest, onSuccess: () => void) {
            editEvents(
                { param: { memberId }, json: payload },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        saveWellness(payload: EditMemberWellnessDetailsRequest, onSuccess: () => void) {
            editWellness(
                { param: { memberId }, json: payload },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        saveShopping(payload: EditMemberShoppingDetailsRequest, onSuccess: () => void) {
            editShopping(
                { param: { memberId }, json: payload },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        savePayments(payload: EditMemberPaymentDetailsRequest, onSuccess: () => void) {
            editPayments(
                { param: { memberId }, json: payload },
                toApiListenerWithToastMessages(() => {
                    void queryClient.invalidateQueries({
                        queryKey: ["memberPaymentPreferences", { param: { memberId } }],
                    });
                    void queryClient.invalidateQueries({
                        queryKey: ["memberStripeAccount", { param: { memberId } }],
                    });
                    onSuccess();
                }),
            );
        },
        createLink(payload: CreateMemberLinkRequest, onSuccess: () => void) {
            createMemberLink(
                { param: { memberId }, json: payload },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        updateLink(
            linkId: string,
            payload: UpdateMemberLinkRequest,
            onSuccess: () => void,
        ) {
            updateMemberLink(
                { param: { memberId, linkId }, json: payload },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        deleteLink(linkId: string, onSuccess: () => void) {
            deleteMemberLink(
                { param: { memberId, linkId } },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        archive(payload: ArchiveMemberRequest, onSuccess: () => void) {
            archiveMember(
                { param: { memberId }, json: payload },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        activate(onSuccess: () => void) {
            activateMember(
                { param: { memberId }, json: {} },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        extendTrial(payload: ExtendTrialRequest, onSuccess: () => void) {
            extendTrialMutation(
                { param: { memberId }, json: payload },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        upgradeMembership(payload: ConvertTrialToPaidRequest, onSuccess: () => void) {
            convertTrialToPaid(
                { param: { memberId }, json: payload },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        saveMembership(payload: UpdateMemberMembershipRequest, onSuccess: () => void) {
            updateMembership(
                { param: { memberId }, json: payload },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        endMembership(payload: CancelMembershipRequest, onSuccess: () => void) {
            cancelMembership(
                { param: { memberId }, json: payload },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
        createReferral(payload: ReferralPromotionRequest, onSuccess: () => void) {
            createReferralPromotion(
                {
                    param: { memberId },
                    json: {
                        popupDescription: payload.popupContent.description,
                        notificationDescription:
                            payload.notificationContent.description,
                    },
                },
                {
                    onSuccess: () => {
                        void queryClient.invalidateQueries({
                            queryKey: ["listMemberReferralPromotions"],
                        });
                        EventEmitter.emit({
                            name: "showToastMessage",
                            params: {
                                type: "info",
                                message: `Promotion created successfully for ${memberName}`,
                            },
                        });
                        onSuccess();
                    },
                    onError: (error) => {
                        Sentry.captureException(error, {
                            extra: { message: "Failed to create a promotion" },
                        });
                        EventEmitter.emit({
                            name: "showToastMessage",
                            params: {
                                type: "error",
                                message: "Promotion creation failed",
                            },
                        });
                    },
                },
            );
        },
        editReferral(
            promotionId: string,
            payload: ReferralPromotionRequest,
            onSuccess: () => void,
        ) {
            updateReferralPromotion(
                {
                    param: { memberId, promotionId },
                    json: {
                        popupDescription: payload.popupContent.description,
                        notificationDescription:
                            payload.notificationContent.description,
                    },
                },
                {
                    onSuccess: () => {
                        void queryClient.invalidateQueries({
                            queryKey: ["listMemberReferralPromotions"],
                        });
                        EventEmitter.emit({
                            name: "showToastMessage",
                            params: {
                                type: "info",
                                message: `Promotion updated successfully for ${memberName}`,
                            },
                        });
                        onSuccess();
                    },
                    onError: (error) => {
                        Sentry.captureException(error, {
                            extra: { message: "Failed to edit a promotion" },
                        });
                        EventEmitter.emit({
                            name: "showToastMessage",
                            params: {
                                type: "error",
                                message: "Promotion update failed",
                            },
                        });
                    },
                },
            );
        },
        setOnboardingByPhone(enable: boolean, onSuccess: () => void) {
            setOnboardingPhoneCompletion(
                { param: { memberId }, json: { enable } },
                toApiListenerWithToastMessages(onSuccess),
            );
        },
    };
}
